import React from 'react';

import { observer } from 'mobx-react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import Grid, { defaultRenderers } from 'components/Grid';

import { ExtendedUserOperators } from 'components/Grid/operators/User/User';

import { ColumnDefinition } from 'components/Grid/types';

import EntityRelatedActionsCell from 'components/SharedFormComponents/EntityRelatedActionsCell';

import { EntityModules, ProtectorType } from 'core/constants';
import { EntityRelatedActionsTypeValue } from 'utils/api/modules/entities/entities.types';

import { Site } from 'containers/Sites/types';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

import EntityActionCell from 'components/Entities/EntityActionCell';
import { entityCreatedColumn } from 'components/Entities/EntityCreatedColumn';

import SiteUnitCell from 'containers/SiteDetails/components/unit/SiteUnitCell';

import { SitesTableProps } from './types';

export const basicSiteColumns = [
  {
    field: 'sites__name',
    headerName: 'Name',
    protectorType: ProtectorType.ShortText,
  },
  {
    field: 'sites__manager',
    headerName: 'Manager',
    renderCell: defaultRenderers.user,
    valueGetter: (value: unknown) => ({ value }),
    filterOperators: ExtendedUserOperators(),
    sortable: false,
    protectorType: ProtectorType.User,
  },
  {
    field: 'relatedActions',
    headerName: 'Actions',
    renderCell: ({ value }: { value: EntityRelatedActionsTypeValue }) => (
      <EntityRelatedActionsCell relatedActions={value.value || { total: 0, completed: 0 }} />
    ),
    valueFormatter: (value: EntityRelatedActionsTypeValue) =>
      `completed:${value?.value?.completed};total:${value?.value?.total}`,
    nativeFilter: true,
    sortable: false,
  },
  entityCreatedColumn(EntityModules.Sites),
] as unknown as ColumnDefinition[];

export const siteUnitColumn = {
  field: 'scopeUnit',
  headerName: 'Unit',
  renderCell: ({ row }: { row: Site }) => <SiteUnitCell unitUuid={row.scopeUnit ?? null} />,
  filterable: false,
  sortable: false,
} as unknown as ColumnDefinition;

const SitesTable: React.FunctionComponent<SitesTableProps> = ({ onError, onRefreshCallback }): React.ReactElement => {
  const { routing, siteDetails, session, organisationStructure } = useStore();
  const showUnit = !!session.user?.scopedPermissions;

  React.useEffect(() => {
    if (showUnit) organisationStructure.loadUnits(false);
  }, [showUnit, organisationStructure]);

  const onRowClick: GridEventListener<'rowClick'> = (item: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.sites.details, { entityId: item.id }));
  };

  return (
    <Grid
      basicColumns={
        [
          ...basicSiteColumns,
          ...(showUnit ? [siteUnitColumn] : []),
          {
            field: 'actions',
            type: 'actions',
            headerName: '',
            maxWidth: 50,
            ariaLabel: 'actions_cell',
            renderCell: ({ row, colDef }: { row: Site; colDef: ColumnDefinition }) => (
              <EntityActionCell rowData={row} actions={colDef.actions} store={siteDetails} />
            ),
            filterOperators: null,
            sortable: false,
          },
        ] as unknown as ColumnDefinition[]
      }
      entity="sites"
      onError={onError}
      dataURL="/api/entities/sites/"
      fieldsURL="/api/entities/sites/fields/"
      disableSelectAllColumns
      customNoRowsText="No sites have been created yet"
      onRowClick={onRowClick}
      onRefreshCallback={onRefreshCallback}
      onlyBasicExport={false}
    />
  );
};

export default observer(SitesTable);
